import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { 
  Users, 
  Plus, 
  Search, 
  Edit2, 
  Trash2,
  Phone,
  Mail,
  Building2,
  Truck,
  Shield,
  ShieldOff,
  DollarSign,
  FileText,
  CreditCard,
  Eye,
  Upload,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";

const BASE_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Customers() {
  const { theme } = useTheme();
  const { businessSlug } = useParams();
  const isDark = theme === "dark";
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [paymentHistoryOpen, setPaymentHistoryOpen] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  const API = `${BASE_API}/b/${businessSlug}`;
  const basePath = `/${businessSlug}`;
  
  const [formData, setFormData] = useState({
    name: "",
    company_name: "",
    email: "",
    phone: "",
    address: "",
    vehicles: [],
    notes: "",
    tax_exempt: false
  });
  const [newVehicle, setNewVehicle] = useState({ year: "", make: "", model: "", vin: "", truck_number: "" });

  useEffect(() => {
    if (businessSlug) {
      fetchCustomers();
    }
  }, [businessSlug]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error("Failed to fetch customers");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, formData);
        toast.success("Customer updated successfully");
      } else {
        await axios.post(`${API}/customers`, formData);
        toast.success("Customer added successfully");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      toast.error(editingCustomer ? "Failed to update customer" : "Failed to add customer");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this customer?")) return;
    try {
      await axios.delete(`${API}/customers/${id}`);
      toast.success("Customer deleted");
      fetchCustomers();
    } catch (error) {
      toast.error("Failed to delete customer");
    }
  };

  const handleViewPaymentHistory = async (customer) => {
    setSelectedCustomer(customer);
    setPaymentHistoryOpen(true);
    try {
      const response = await axios.get(`${API}/customers/${customer.id}/payment-history`);
      setPaymentHistory(response.data);
    } catch (error) {
      toast.error("Failed to load payment history");
      setPaymentHistory(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name || "",
      company_name: customer.company_name || "",
      email: customer.email || "",
      phone: customer.phone || "",
      address: customer.address || "",
      vehicles: customer.vehicles || [],
      notes: customer.notes || "",
      tax_exempt: customer.tax_exempt || false
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCustomer(null);
    setFormData({
      name: "",
      company_name: "",
      email: "",
      phone: "",
      address: "",
      vehicles: [],
      notes: "",
      tax_exempt: false
    });
    setNewVehicle({ year: "", make: "", model: "", vin: "", truck_number: "" });
  };

  const addVehicle = () => {
    if (newVehicle.make || newVehicle.truck_number) {
      setFormData({
        ...formData,
        vehicles: [...formData.vehicles, { ...newVehicle }]
      });
      setNewVehicle({ year: "", make: "", model: "", vin: "", truck_number: "" });
    }
  };

  const removeVehicle = (index) => {
    setFormData({
      ...formData,
      vehicles: formData.vehicles.filter((_, i) => i !== index)
    });
  };

  const filteredCustomers = customers
    .filter((customer) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        customer.name.toLowerCase().includes(searchLower) ||
        (customer.company_name && customer.company_name.toLowerCase().includes(searchLower)) ||
        (customer.phone && customer.phone.includes(searchTerm)) ||
        (customer.email && customer.email.toLowerCase().includes(searchLower))
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name)); // Sort A-Z by name

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="customers-loading">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
            Customers
          </h1>
          <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            {customers.length} customers
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Import — surfaced here (per user request) instead of a
              separate sidebar item. Tooltip explains exactly what the
              upload accepts. */}
          <Link to={`${basePath}/import?type=customers`}>
            <Button
              variant="outline"
              className={isDark ? 'border-slate-700 text-slate-200 hover:bg-slate-800' : ''}
              data-testid="import-customers-btn"
              title="Bulk-upload a CSV/Excel/PDF of customers — we'll auto-map name, email, phone, address. Skips duplicates by email."
            >
              <Upload className="w-4 h-4 mr-2" />
              Import Customers
            </Button>
          </Link>
          <Button 
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            onClick={() => { resetForm(); setIsDialogOpen(true); }}
            data-testid="add-customer-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Customer
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, company, phone, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`pl-10 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}
              data-testid="search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className={isDark ? 'border-slate-700' : 'border-slate-200'}>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-slate-600'}>Name</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-slate-600'}>Company</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-slate-600'}>Contact</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-slate-600'}>Tax Status</TableHead>
                  <TableHead className={`text-right ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className={`text-center py-12 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No customers found</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCustomers.map((customer) => (
                    <TableRow 
                      key={customer.id} 
                      onClick={() => handleViewPaymentHistory(customer)}
                      className={`table-row-hover cursor-pointer ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                      data-testid={`customer-row-${customer.id}`}
                      title="Click to see all invoices for this customer"
                    >
                      <TableCell className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
                        {customer.name}
                      </TableCell>
                      <TableCell className={isDark ? 'text-slate-300' : 'text-slate-600'}>
                        {customer.company_name || "-"}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {customer.phone && (
                            <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                              <Phone className="w-3 h-3" />
                              {customer.phone}
                            </div>
                          )}
                          {customer.email && (
                            <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                              <Mail className="w-3 h-3" />
                              {customer.email}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {customer.tax_exempt ? (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                            <ShieldOff className="w-3 h-3 mr-1" />
                            Tax Exempt
                          </Badge>
                        ) : (
                          <Badge variant="outline" className={isDark ? 'border-slate-600 text-slate-400' : 'border-slate-300 text-slate-500'}>
                            <Shield className="w-3 h-3 mr-1" />
                            Taxable
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewPaymentHistory(customer)}
                            className="hover:bg-green-50 hover:text-green-600 dark:hover:bg-slate-700"
                            data-testid={`payment-history-${customer.id}`}
                            title="View Payment History"
                          >
                            <DollarSign className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(customer)}
                            className="hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-slate-700"
                            data-testid={`edit-customer-${customer.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(customer.id)}
                            className="hover:bg-red-50 hover:text-red-600 dark:hover:bg-slate-700"
                            data-testid={`delete-customer-${customer.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-slate-800'}>
              {editingCustomer ? "Edit Customer" : "Add New Customer"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Company Info - All Optional */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Customer/Company Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                  placeholder="Name or Company"
                  data-testid="input-name"
                />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Company Name</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                  placeholder="e.g., Ultra Express"
                  data-testid="input-company"
                />
              </div>
            </div>

            {/* Contact Info - All Optional */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Phone Number</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                  placeholder="Phone number"
                  data-testid="input-phone"
                />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                  placeholder="Email address"
                  data-testid="input-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                placeholder="Address"
                data-testid="input-address"
              />
            </div>

            {/* Tax Exempt Toggle */}
            <div className={`flex items-center justify-between p-4 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-slate-50'}`}>
              <div className="flex items-center gap-3">
                {formData.tax_exempt ? (
                  <ShieldOff className="w-5 h-5 text-amber-500" />
                ) : (
                  <Shield className="w-5 h-5 text-green-500" />
                )}
                <div>
                  <Label className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>Tax Exempt Status</Label>
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    {formData.tax_exempt ? "Customer is tax exempt" : "Normal tax applies"}
                  </p>
                </div>
              </div>
              <Switch
                checked={formData.tax_exempt}
                onCheckedChange={(checked) => setFormData({ ...formData, tax_exempt: checked })}
                data-testid="tax-exempt-toggle"
              />
            </div>

            {/* Vehicles Section */}
            <div className="space-y-3">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Vehicles</Label>
              {formData.vehicles.length > 0 && (
                <div className="space-y-2">
                  {formData.vehicles.map((v, i) => (
                    <div key={i} className={`flex items-center justify-between p-2 rounded border ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                      <span className={`text-sm ${isDark ? 'text-white' : 'text-slate-800'}`}>
                        {v.truck_number && <span className="font-medium text-blue-600">#{v.truck_number}</span>}
                        {v.truck_number && (v.year || v.make || v.model) && " - "}
                        {v.year} {v.make} {v.model}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeVehicle(i)}
                        className="text-red-500 hover:text-red-600 h-6 px-2"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-5 gap-2">
                <Input
                  placeholder="Truck #"
                  value={newVehicle.truck_number}
                  onChange={(e) => setNewVehicle({ ...newVehicle, truck_number: e.target.value })}
                  className={`text-sm ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}
                />
                <Input
                  placeholder="Year"
                  value={newVehicle.year}
                  onChange={(e) => setNewVehicle({ ...newVehicle, year: e.target.value })}
                  className={`text-sm ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}
                />
                <Input
                  placeholder="Make"
                  value={newVehicle.make}
                  onChange={(e) => setNewVehicle({ ...newVehicle, make: e.target.value })}
                  className={`text-sm ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}
                />
                <Input
                  placeholder="Model"
                  value={newVehicle.model}
                  onChange={(e) => setNewVehicle({ ...newVehicle, model: e.target.value })}
                  className={`text-sm ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addVehicle}
                  className={isDark ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-200 hover:bg-slate-50'}
                  data-testid="add-vehicle-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className={`min-h-[80px] ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}
                data-testid="input-notes"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className={isDark ? 'border-slate-600' : 'border-slate-200'}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="submit-customer-btn"
              >
                {editingCustomer ? "Update" : "Add Customer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment History Dialog */}
      <Dialog open={paymentHistoryOpen} onOpenChange={setPaymentHistoryOpen}>
        <DialogContent className={`max-w-3xl max-h-[80vh] overflow-y-auto ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
              <DollarSign className="w-5 h-5 text-green-500" />
              Payment History - {selectedCustomer?.name}
            </DialogTitle>
          </DialogHeader>
          
          {paymentHistory ? (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <Card className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50'}>
                  <CardContent className="p-4 text-center">
                    <p className={`text-xs uppercase ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Total Invoiced</p>
                    <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                      {formatCurrency(paymentHistory.summary.total_invoiced)}
                    </p>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      {paymentHistory.summary.invoice_count} invoices
                    </p>
                  </CardContent>
                </Card>
                <Card className={`${isDark ? 'bg-green-900/30 border-green-700' : 'bg-green-50 border-green-200'}`}>
                  <CardContent className="p-4 text-center">
                    <p className={`text-xs uppercase ${isDark ? 'text-green-400' : 'text-green-600'}`}>Total Paid</p>
                    <p className="text-xl font-bold text-green-500">
                      {formatCurrency(paymentHistory.summary.total_paid)}
                    </p>
                    <p className={`text-xs ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                      {paymentHistory.summary.paid_invoice_count} paid
                    </p>
                  </CardContent>
                </Card>
                <Card className={`${isDark ? 'bg-amber-900/30 border-amber-700' : 'bg-amber-50 border-amber-200'}`}>
                  <CardContent className="p-4 text-center">
                    <p className={`text-xs uppercase ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>Pending</p>
                    <p className="text-xl font-bold text-amber-500">
                      {formatCurrency(paymentHistory.summary.total_pending)}
                    </p>
                    <p className={`text-xs ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                      {paymentHistory.summary.pending_invoice_count} pending
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Payments */}
              {paymentHistory.payments.length > 0 && (
                <div>
                  <h3 className={`font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                    <CreditCard className="w-4 h-4 text-green-500" />
                    Recent Payments
                  </h3>
                  <div className="space-y-2">
                    {paymentHistory.payments.slice(0, 5).map((payment, idx) => (
                      <div 
                        key={idx} 
                        className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-slate-50'}`}
                      >
                        <div>
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
                            {formatCurrency(payment.amount)}
                          </p>
                          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                            Invoice #{payment.invoice_number} • {payment.method}
                          </p>
                        </div>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          {formatDate(payment.date)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Invoices List */}
              <div>
                <h3 className={`font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  <FileText className="w-4 h-4 text-blue-500" />
                  All Invoices
                </h3>
                <Table>
                  <TableHeader>
                    <TableRow className={isDark ? 'border-slate-600' : ''}>
                      <TableHead className={isDark ? 'text-slate-300' : ''}>Invoice #</TableHead>
                      <TableHead className={isDark ? 'text-slate-300' : ''}>Date</TableHead>
                      <TableHead className={isDark ? 'text-slate-300' : ''}>Status</TableHead>
                      <TableHead className={`text-right ${isDark ? 'text-slate-300' : ''}`}>Total</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paymentHistory.invoices.map((invoice) => (
                      <TableRow key={invoice.id} className={isDark ? 'border-slate-600' : ''}>
                        <TableCell className={`font-medium ${isDark ? 'text-white' : ''}`}>
                          {invoice.invoice_number}
                        </TableCell>
                        <TableCell className={isDark ? 'text-slate-300' : ''}>
                          {formatDate(invoice.created_at)}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            invoice.payment_status === 'paid' 
                              ? 'bg-green-100 text-green-700' 
                              : invoice.payment_status === 'partial'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-amber-100 text-amber-700'
                          }>
                            {invoice.payment_status}
                          </Badge>
                        </TableCell>
                        <TableCell className={`text-right font-medium ${isDark ? 'text-white' : ''}`}>
                          {formatCurrency(invoice.total)}
                        </TableCell>
                        <TableCell>
                          <Link to={`${basePath}/invoices/${invoice.id}`}>
                            <Button variant="ghost" size="sm" className="text-blue-500">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
